import React, { useEffect, useState, useContext } from 'react';
import { isEmpty, reduce } from 'lodash';
import { LocaleService } from '@sm360/widget-commons-core-services';
import { CheckboxGroup } from '@sm360/ui-library';
import { AnalyticsService, TRACKING_TAGS as TAGS, VIRTUAL_PAGEVIEWS as PAGES } from '@sm360/widget-commons-general';
import 'trade-in-360/components/InstantEstimateWrapper/InstantEstimateWrapper.scss';
import 'trade-in-360/components/InstantEstimateWrapper/Steps/VehicleOptions/VehicleOptions.scss';
import WEB_CSS_CLASSNAMES from 'trade-in-360/constants/webCssClassnames.js';
import NewTradeInContext from '../../../../NewTradeInContext.js';
import ActionButton from '../../Components/ActionButton/ActionButton.jsx';

const VehicleOptions = ({
    tradeInVehicle,
    onPreviousStep,
    onNextStep,
    onFormSubmit,
    previousLabel,
    nextLabel,
    noTitle,
    setVehicleOptions,
    isTrade360,
}) => {
    const [selectedTradeInVehicle, setTradeInVehicle] = useState([]);
    const [availableVehicleOptions, setAvailableVehicleOptions] = useState([]);
    const { fieldsLocked } = useContext(NewTradeInContext);

    const onCheckBoxChange = (values) => {
        const selectedOptions = [];
        // To filter selected options from original trade-in props and set with selectedTradeInVehicle obj
        values.forEach((value) => {
            selectedOptions.push(selectedTradeInVehicle.options.filter((option) => value === option.code)[0]);
        });
        // set selectedVehicleOptions in tradeInVehicle obj to differenciate available options and selected options from this screen
        selectedTradeInVehicle.selectedVehicleOptions = selectedOptions;

        // For Trade360 vehicle options display on vehicle info page
        // Pass selected options to vehicleInfo component to store into state
        // Only use when VehicleOptions component rendered into VehicleInfo component
        if (setVehicleOptions) setVehicleOptions(selectedOptions);
        setTradeInVehicle(selectedTradeInVehicle);
    };

    // Send selected and previous screen trade-in option to the next screen
    const sendVehicleOptionsForm = (event) => {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_NEXT_BTN());
        event.preventDefault();
        if (fieldsLocked) return onNextStep();

        return onFormSubmit(selectedTradeInVehicle);
    };

    // Update state with props from parent component for trade-in vehicle
    const getTradeInVehicle = (updatedVehicle) => {
        const tradeVehicle = updatedVehicle || selectedTradeInVehicle;
        const vehicleOptions = reduce(
            tradeInVehicle.options,
            (acc, result) => {
                acc.push({
                    id: result.code,
                    label: result.description,
                    value: result.description,
                    disabled: fieldsLocked,
                });
                return acc;
            },
            []
        );
        if (!isEmpty(tradeVehicle)) {
            setTradeInVehicle(tradeVehicle);
            setAvailableVehicleOptions(vehicleOptions);
        }
    };

    // Get already checked vehicle options
    // This will set the checked options if move from different screen
    const getCheckedOptions = () => {
        return selectedTradeInVehicle?.selectedVehicleOptions?.map((options) => options.code);
    };

    useEffect(() => {
        getTradeInVehicle(tradeInVehicle);
        AnalyticsService.virtualPageView(PAGES.ESTIMATED_TRADEIN_OPTION());
    }, []);

    const translateSubTitle = isTrade360
        ? 'TRADE_IN.VEHICLE_OPTIONS.SUBTITLE'
        : 'TRADE_IN.VEHICLE_OPTIONS.SUBTITLE_DILAWRI';
    return (
        <div className="vehicle-options-form">
            {!noTitle && <h5>{LocaleService.t(`TRADE_IN.SECTIONS.VEHICLE_OPTIONS`)}</h5>}
            <form className="vehicle-options-checkbox-form">
                <div className="vehicle-options-form-body">
                    {availableVehicleOptions.length > 0 ? (
                        <section className="clearfix vehicle-options-form-body--vehicle-details">
                            <p
                                className={`${
                                    isTrade360 ? WEB_CSS_CLASSNAMES.HEADING4 : 'sr-text vehicle-options-subtitle'
                                }`}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: LocaleService.t(translateSubTitle),
                                }}
                            />
                            <CheckboxGroup
                                inline={!isTrade360}
                                name="vehicleOptionsCheckboxGroup"
                                options={availableVehicleOptions}
                                onChange={(value) => onCheckBoxChange(value)}
                                checked={getCheckedOptions()}
                            />
                        </section>
                    ) : (
                        <p className="sr-text">{LocaleService.t('TRADE_IN.VEHICLE_OPTIONS.OPTIONS_NOT_AVAILABLE')}</p>
                    )}
                    {onNextStep &&
                        onPreviousStep && (
                            <section
                                className="clearfix vehicle-buttons-container"
                                data-test="trade-in.vehicle-form.submit"
                            >
                                <div className="vehicle-buttons">
                                    <ActionButton onClick={() => onPreviousStep()}>{previousLabel}</ActionButton>
                                    <ActionButton next onClick={sendVehicleOptionsForm}>
                                        {nextLabel}
                                    </ActionButton>
                                </div>
                            </section>
                        )}
                </div>
            </form>
        </div>
    );
};

VehicleOptions.displayName = 'VehicleOptions';

export default VehicleOptions;
